import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { isAdminEmailMockTransportEnabled } from "../../config/adminEmailMock";
import { emailAnalysisStepDomId } from "../../utils/emailAnalysisAnchors";
import {
  normalizeEmailThread,
  pickLatestInboundOfferInsight,
} from "../../utils/adminEmailThreadNormalize";
import { buildClientEmailDraftFromAiAnalysis } from "../../utils/aiAnalysisInsights";
import {
  budgetAssessmentPillClass,
  budgetStatusDisplayLabel,
} from "../../utils/adminClientAnalysisExtended";
import {
  isProbablyHtml,
  plainTextFromHtml,
  plainTextToEmailBodyHtml,
  sanitizeEmailHtml,
} from "../../utils/emailHtml";
import { AdminEmailRichComposer } from "./AdminEmailRichComposer";

export { normalizeEmailThread } from "../../utils/adminEmailThreadNormalize";

function MailGlyph() {
  return (
    <svg
      className="admin-dash__email-hero-svg"
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M4 6h16v12H4V6z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path
        d="M4 7l8 5 8-5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function stripOfferClass(signal) {
  if (signal === "accept_offer") return "admin-dash__email-reply-strip__offer--accept";
  if (signal === "reject_offer") return "admin-dash__email-reply-strip__offer--reject";
  if (signal === "negotiate") return "admin-dash__email-reply-strip__offer--negotiate";
  return "admin-dash__email-reply-strip__offer--unclear";
}

function stripToneClass(verdict) {
  if (verdict === "favorable") return "admin-dash__email-reply-strip__tone--ok";
  if (verdict === "needs_attention") return "admin-dash__email-reply-strip__tone--alert";
  return "admin-dash__email-reply-strip__tone--mixed";
}

function clientVerdictLabel(verdict) {
  if (verdict === "favorable") return "Favorable read";
  if (verdict === "needs_attention") return "Needs attention";
  return "Mixed / clarify";
}

function offerSignalClass(signal) {
  if (signal === "accept_offer") return "admin-dash__email-offer-pill--accept";
  if (signal === "reject_offer") return "admin-dash__email-offer-pill--reject";
  if (signal === "negotiate") return "admin-dash__email-offer-pill--negotiate";
  return "admin-dash__email-offer-pill--unclear";
}

function clipText(s, max) {
  const t = (s || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** @param {{ status?: string } | null | undefined} ca */
function inboundAnalysisUiKind(ca) {
  if (!ca || typeof ca !== "object") return "waiting";
  const s = String(ca.status || "").toLowerCase();
  if (s === "pending") return "pending";
  if (s === "failed") return "failed";
  if (s === "completed") return "completed";
  return "waiting";
}

function ThreadMessageBody({ text }) {
  const raw = text || "";
  if (isProbablyHtml(raw)) {
    return (
      <div
        className="admin-dash__email-mailcard-html"
        dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(raw) }}
      />
    );
  }
  const { lead, quoted } = splitQuotedReplyText(raw);
  return (
    <div className="admin-dash__email-mailcard-plain">
      <div className="admin-dash__email-mailcard-plain-main">{lead || "—"}</div>
      {quoted ? <pre className="admin-dash__email-mailcard-plain-quoted">{quoted}</pre> : null}
    </div>
  );
}

function splitQuotedReplyText(raw) {
  const text = String(raw || "").trim();
  if (!text) return { lead: "", quoted: "" };
  const lines = text.split(/\r?\n/);
  let quoteStart = -1;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (
      /^>\s?/.test(line) ||
      /^on .+ wrote:$/i.test(line) ||
      /^from:\s+/i.test(line) ||
      /^sent:\s+/i.test(line) ||
      /^subject:\s+/i.test(line) ||
      /^-{2,}\s*original message\s*-{2,}$/i.test(line)
    ) {
      quoteStart = i;
      break;
    }
  }
  if (quoteStart < 0) return { lead: text, quoted: "" };
  const lead = lines.slice(0, quoteStart).join("\n").trim();
  const quoted = lines.slice(quoteStart).join("\n").trim();
  return { lead, quoted };
}

/**
 * Client-visible email thread + compose (backend: GET detail `emailMessages`, POST …/emails, POST …/analysis/run).
 */
export function AdminEmailThreadPanel({
  emailMessages,
  clientEmail,
  defaultSubject,
  onSendEmail,
  sending = false,
  /** @type {((emailMessageId: string) => Promise<unknown>) | undefined} */
  onRerunClientAnalysis,
  /** When set (service request id), inbound rows show a link to email-analysis detail + anchor for this message. */
  emailAnalysisRequestId,
  /** Service-request-level `aiAnalysis` — used to suggest a first client email when there is no inbound reply yet. */
  requestAiAnalysis,
}) {
  const [subject, setSubject] = useState(defaultSubject || "");
  const [bodyHtml, setBodyHtml] = useState("");
  const [rerunBusyId, setRerunBusyId] = useState(null);
  const [copiedSuggestionForId, setCopiedSuggestionForId] = useState(null);
  const topMessageRef = useRef(null);
  const prevTopMessageIdRef = useRef(null);

  const mockTransport = isAdminEmailMockTransportEnabled();

  useEffect(() => {
    setSubject(defaultSubject || "");
  }, [defaultSubject]);

  const thread = useMemo(
    () => normalizeEmailThread(Array.isArray(emailMessages) ? emailMessages : [], clientEmail),
    [emailMessages, clientEmail]
  );
  const displayThread = useMemo(
    () =>
      [...thread].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [thread]
  );

  const to = (clientEmail || "").trim();
  const bodyPlainLen = plainTextFromHtml(bodyHtml).length;
  const canSend = Boolean(to && onSendEmail && bodyPlainLen > 0 && !sending);
  const inboundCount = thread.filter((m) => m.direction === "inbound").length;
  const outboundCount = thread.filter((m) => m.direction === "outbound").length;

  const headerOfferInsight = useMemo(
    () => pickLatestInboundOfferInsight(thread),
    [thread]
  );

  /**
   * Draft for composer: inbound budget suggestion first; if the client has not replied yet,
   * fall back to a first-outreach email built from `requestAiAnalysis`.
   */
  const { suggestedClientReplyDraft, suggestedDraftSource } = useMemo(() => {
    const inbound = thread.filter((m) => m.direction === "inbound");
    let bestText = "";
    let bestTime = -1;
    for (const m of inbound) {
      const raw = m.clientAnalysis?.budgetAssessment?.suggestedClientReply;
      const reply = typeof raw === "string" ? raw.trim() : "";
      if (!reply) continue;
      const t = new Date(m.createdAt || 0).getTime();
      if (t >= bestTime) {
        bestTime = t;
        bestText = reply;
      }
    }
    if (bestText) {
      return { suggestedClientReplyDraft: bestText, suggestedDraftSource: "inbound_budget" };
    }
    if (inbound.length === 0 && requestAiAnalysis) {
      const fromRequest = buildClientEmailDraftFromAiAnalysis(requestAiAnalysis);
      if (fromRequest) {
        return { suggestedClientReplyDraft: fromRequest, suggestedDraftSource: "request_ai" };
      }
    }
    return { suggestedClientReplyDraft: null, suggestedDraftSource: null };
  }, [thread, requestAiAnalysis]);

  const handleRerunAnalysis = useCallback(
    async (msg) => {
      if (!onRerunClientAnalysis || !msg?.id) return;
      setRerunBusyId(msg.id);
      try {
        await onRerunClientAnalysis(msg.id);
      } finally {
        setRerunBusyId(null);
      }
    },
    [onRerunClientAnalysis]
  );

  const insertPlainSuggestedIntoComposer = useCallback((plainText) => {
    if (!plainText) return;
    const chunk = isProbablyHtml(plainText)
      ? sanitizeEmailHtml(plainText)
      : plainTextToEmailBodyHtml(plainText);
    setBodyHtml((prev) => {
      const cur = (prev || "").trim();
      if (!cur) return chunk;
      return `${cur}<p><br /></p>${chunk}`;
    });
  }, []);

  const copyInboundSuggestion = useCallback(async (messageId, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSuggestionForId(messageId);
      window.setTimeout(() => {
        setCopiedSuggestionForId((id) => (id === messageId ? null : id));
      }, 2000);
    } catch {
      /* ignore */
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    try {
      await onSendEmail({
        subject: (subject || defaultSubject || "").trim() || defaultSubject,
        body: sanitizeEmailHtml(bodyHtml),
      });
      setBodyHtml("");
      setSubject(defaultSubject || "");
    } catch {
      /* toast from thunk */
    }
  };

  const showEmpty = displayThread.length === 0;
  const topMessageId = displayThread[0]?.id ?? null;

  useEffect(() => {
    if (!topMessageId || prevTopMessageIdRef.current === topMessageId) return;
    prevTopMessageIdRef.current = topMessageId;
    if (topMessageRef.current) {
      topMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [topMessageId]);

  return (
    <section id="request-email-thread" className="admin-dash__panel admin-dash__panel--email">
      <header className="admin-dash__email-hero">
        <div className="admin-dash__email-hero-icon" aria-hidden>
          <MailGlyph />
        </div>
        <div className="admin-dash__email-hero-copy">
          <div className="admin-dash__email-hero-title-row">
            <h2 className="admin-dash__email-hero-title">Email the client</h2>
            {headerOfferInsight ? (
              <span
                className={`admin-dash__email-hero-offer ${offerSignalClass(headerOfferInsight.signal)}`}
                title="From the latest analyzed client message"
              >
                {headerOfferInsight.label}
              </span>
            ) : null}
          </div>
          {inboundCount > 0 && !headerOfferInsight ? (
            <p className="admin-dash__email-hero-offer-hint">
              Offer stance appears here when the latest inbound message has completed analysis from your API.
            </p>
          ) : null}
          <p className="admin-dash__email-hero-lede">
            Sends via <code className="admin-dash__code-inline">POST …/admin/…/emails</code>. Thread and per-message
            analysis come from <code className="admin-dash__code-inline">emailMessages</code> on{" "}
            <code className="admin-dash__code-inline">GET …/:id</code> (inbound mail is ingested by your server /
            webhook).
            {mockTransport ? (
              <>
                {" "}
                <strong>Mock send transport is on</strong> (<code className="admin-dash__code-inline">
                  REACT_APP_ADMIN_EMAIL_MOCK=true
                </code>
                ): sends do <strong>not</strong> call your API; the app simulates delivery and fake client replies
                locally.
              </>
            ) : null}
          </p>
        </div>
        <div className="admin-dash__email-hero-chips" aria-label="Thread summary">
          <span className="admin-dash__email-chip">
            {thread.length} {thread.length === 1 ? "message" : "messages"}
          </span>
          {thread.length > 0 ? (
            <>
              <span className="admin-dash__email-chip admin-dash__email-chip--muted">
                {outboundCount} sent
              </span>
              <span className="admin-dash__email-chip admin-dash__email-chip--muted">
                {inboundCount} from client
              </span>
            </>
          ) : null}
        </div>
      </header>

      <div className="admin-dash__email-layout">
        <div className="admin-dash__email-col admin-dash__email-col--thread">
          <div className="admin-dash__email-col-head">
            <h3 className="admin-dash__email-col-title">Conversation</h3>
            <p className="admin-dash__email-col-hint">
              Inbound rows show <code className="admin-dash__code-inline">clientAnalysis</code> from the API;
              Re-analyse calls <code className="admin-dash__code-inline">POST …/emails/:id/analysis/run</code>.
            </p>
          </div>

          <div className="admin-dash__email-thread" role="log" aria-label="Email thread">
            {showEmpty ? (
              <div className="admin-dash__email-empty">
                <p className="admin-dash__email-empty-title">No emails yet</p>
                <p className="admin-dash__email-empty-sub">
                  After you send from here or the client replies, messages show up from{" "}
                  <code className="admin-dash__code-inline">emailMessages</code> on the record detail API.
                </p>
              </div>
            ) : (
              <ul className="admin-dash__email-thread-list">
                {displayThread.map((msg, idx) => (
                  <li
                    key={msg.id}
                    ref={idx === 0 ? topMessageRef : null}
                    className={`admin-dash__email-thread-item${
                      msg.direction === "inbound" ? " admin-dash__email-thread-item--inbound" : ""
                    }`}
                  >
                    {msg.direction === "outbound" ? (
                      <article className="admin-dash__email-mailcard admin-dash__email-mailcard--outbound">
                        <div className="admin-dash__email-mailcard-top">
                          <div className="admin-dash__email-mailcard-labels">
                            <span className="admin-dash__email-mailcard-badge admin-dash__email-mailcard-badge--sent">
                              You sent
                            </span>
                          </div>
                          <time className="admin-dash__email-mailcard-time" dateTime={msg.createdAt}>
                            {new Date(msg.createdAt).toLocaleString()}
                          </time>
                        </div>
                        {msg.subject ? (
                          <h4 className="admin-dash__email-mailcard-subject">{msg.subject}</h4>
                        ) : null}
                        <ThreadMessageBody text={msg.body} />
                        {(msg.from || msg.to) && msg.from ? (
                          <footer className="admin-dash__email-mailcard-footer">
                            {msg.from ? <span>From {msg.from}</span> : null}
                            {msg.from && msg.to ? <span aria-hidden> · </span> : null}
                            {msg.to ? <span>To {msg.to}</span> : null}
                          </footer>
                        ) : null}
                      </article>
                    ) : (
                      <div className="admin-dash__email-inbound-bundle">
                        <article className="admin-dash__email-mailcard admin-dash__email-mailcard--inbound">
                          <div className="admin-dash__email-mailcard-top">
                            <div className="admin-dash__email-mailcard-labels">
                              <span className="admin-dash__email-mailcard-badge admin-dash__email-mailcard-badge--client">
                                From client
                              </span>
                            </div>
                            <time className="admin-dash__email-mailcard-time" dateTime={msg.createdAt}>
                              {new Date(msg.createdAt).toLocaleString()}
                            </time>
                          </div>
                          {msg.subject ? (
                            <h4 className="admin-dash__email-mailcard-subject">{msg.subject}</h4>
                          ) : null}
                          <ThreadMessageBody text={msg.body} />
                          {(msg.from || msg.to) && (msg.direction === "inbound" || msg.from) ? (
                            <footer className="admin-dash__email-mailcard-footer">
                              {msg.from ? <span>From {msg.from}</span> : null}
                              {msg.from && msg.to ? <span aria-hidden> · </span> : null}
                              {msg.to ? <span>To {msg.to}</span> : null}
                            </footer>
                          ) : null}
                        </article>

                        <section
                          className="admin-dash__email-reply-strip"
                          aria-label={`Client reply analysis for message ${msg.id}`}
                        >
                          <div className="admin-dash__email-reply-strip__row">
                            <span className="admin-dash__email-reply-strip__tag" title="AI read (client reply)">
                              AI
                            </span>
                            {(() => {
                              const ca = msg.clientAnalysis;
                              const kind = inboundAnalysisUiKind(ca);
                              if (kind === "pending") {
                                const ml = ca?.modelLabel;
                                return (
                                  <span
                                    className="admin-dash__email-reply-strip__analyse"
                                    role="status"
                                    aria-live="polite"
                                    aria-label={ml ? `Analysing with ${ml}` : "Analysing"}
                                  >
                                    <span className="admin-dash__email-gemini-spinner" aria-hidden="true" />
                                    <span className="admin-dash__email-reply-strip__analyse-copy">
                                      <span className="admin-dash__email-reply-strip__status admin-dash__email-reply-strip__status--with-spinner">
                                        Analysing…
                                      </span>
                                      <span className="admin-dash__email-reply-strip__model" title="Model">
                                        {ml || "—"}
                                      </span>
                                    </span>
                                  </span>
                                );
                              }
                              if (kind === "failed" && ca) {
                                return (
                                  <span className="admin-dash__email-reply-strip__fail-block" role="alert">
                                    <span className="admin-dash__email-reply-strip__model admin-dash__email-reply-strip__model--error">
                                      {ca.modelLabel || "—"}
                                    </span>
                                    <span
                                      className="admin-dash__email-reply-strip__error"
                                      title={ca.errorMessage || ""}
                                    >
                                      {clipText(ca.errorMessage || "Analysis failed", 120)}
                                    </span>
                                  </span>
                                );
                              }
                              if (kind === "completed" && ca) {
                                const verdict = ca.verdict || "mixed";
                                const offerSig = ca.offerSignal || "unclear";
                                return (
                                  <>
                                    {ca.offerLabel ? (
                                      <span
                                        className={`admin-dash__email-reply-strip__offer ${stripOfferClass(offerSig)}`}
                                      >
                                        {clipText(ca.offerLabel, 50)}
                                      </span>
                                    ) : null}
                                    <span
                                      className={`admin-dash__email-reply-strip__tone ${stripToneClass(verdict)}`}
                                    >
                                      {clientVerdictLabel(verdict)}
                                    </span>
                                    <span className="admin-dash__email-reply-strip__meta" title="Sentiment · intent">
                                      {ca.sentimentLabel || "—"} · {clipText(ca.clientIntent || "—", 48)}
                                    </span>
                                    <span
                                      className="admin-dash__email-reply-strip__model"
                                      title="Model used for this read"
                                    >
                                      {ca.modelLabel || "—"}
                                    </span>
                                    {typeof ca.confidence === "number" ? (
                                      <span className="admin-dash__email-reply-strip__conf" title="Model confidence">
                                        {Math.round(ca.confidence * 100)}% confidence
                                      </span>
                                    ) : null}
                                  </>
                                );
                              }
                              return (
                                <span className="admin-dash__email-reply-strip__status">
                                  Waiting for analysis…
                                </span>
                              );
                            })()}
                            <div className="admin-dash__email-reply-strip__row-actions">
                              {emailAnalysisRequestId ? (
                                <Link
                                  className="admin-dash__email-reply-strip__btn admin-dash__email-reply-strip__btn--link"
                                  to={`/admin/dashboard/email-analysis/${encodeURIComponent(
                                    emailAnalysisRequestId
                                  )}#${emailAnalysisStepDomId(msg.id)}`}
                                  title="Open full AI analysis for this message"
                                >
                                  Full analysis
                                </Link>
                              ) : null}
                              <button
                                type="button"
                                className="admin-dash__email-reply-strip__btn"
                                onClick={() => handleRerunAnalysis(msg)}
                                disabled={
                                  !msg.body.trim() ||
                                  rerunBusyId === msg.id ||
                                  !onRerunClientAnalysis
                                }
                                title={
                                  onRerunClientAnalysis
                                    ? undefined
                                    : "Wire onRerunClientAnalysis to POST …/analysis/run"
                                }
                              >
                                {rerunBusyId === msg.id ? "Queuing…" : "Re-analyse"}
                              </button>
                            </div>
                          </div>
                          {msg.clientAnalysis?.status === "completed" && msg.clientAnalysis?.summary?.trim() ? (
                            <p className="admin-dash__email-reply-strip__summary-line">
                              {msg.clientAnalysis.summary.trim()}
                            </p>
                          ) : null}
                          {msg.clientAnalysis?.status === "completed" &&
                          (msg.clientAnalysis?.budgetAssessment?.status ||
                            msg.clientAnalysis?.professionalSuggestion?.requiredProfessionalLevel) ? (
                            <div className="admin-dash__email-reply-strip__extra-hints">
                              {msg.clientAnalysis?.budgetAssessment?.status ? (
                                <span
                                  className={`admin-dash__email-analysis-budget-pill admin-dash__email-analysis-budget-pill--strip ${budgetAssessmentPillClass(
                                    msg.clientAnalysis.budgetAssessment.status
                                  )}`}
                                >
                                  Budget · {budgetStatusDisplayLabel(msg.clientAnalysis.budgetAssessment.status)}
                                </span>
                              ) : null}
                              {msg.clientAnalysis?.professionalSuggestion?.requiredProfessionalLevel ? (
                                <span className="admin-dash__email-reply-strip__level-hint">
                                  Level · {msg.clientAnalysis.professionalSuggestion.requiredProfessionalLevel}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                          {msg.clientAnalysis?.status === "completed"
                            ? (() => {
                                const ca = msg.clientAnalysis;
                                const ps = ca?.professionalSuggestion;
                                const ba = ca?.budgetAssessment;
                                const svc =
                                  ps && typeof ps.recommendedService === "string"
                                    ? ps.recommendedService.trim()
                                    : "";
                                const why =
                                  ps && typeof ps.whyThisService === "string" ? ps.whyThisService.trim() : "";
                                const proLine = svc || (why ? clipText(why, 240) : "");
                                const suggestedReply =
                                  ba && typeof ba.suggestedClientReply === "string"
                                    ? ba.suggestedClientReply.trim()
                                    : "";
                                if (!proLine && !suggestedReply) return null;
                                return (
                                  <div
                                    className="admin-dash__email-reply-strip__per-msg-suggestions"
                                    aria-label="AI suggestions for this client message"
                                  >
                                    {proLine ? (
                                      <p className="admin-dash__email-reply-strip__pro-suggest-line">
                                        <span className="admin-dash__email-reply-strip__suggest-k">
                                          Professional fit
                                        </span>
                                        {proLine}
                                      </p>
                                    ) : null}
                                    {suggestedReply ? (
                                      <div className="admin-dash__email-reply-strip__suggested-reply-wrap">
                                        <div className="admin-dash__email-reply-strip__suggested-reply-head">
                                          <span className="admin-dash__email-reply-strip__suggest-k">
                                            Suggested reply
                                          </span>
                                          <span className="admin-dash__email-reply-strip__suggested-reply-actions">
                                            <button
                                              type="button"
                                              className="admin-dash__email-reply-strip__suggest-btn"
                                              onClick={() => copyInboundSuggestion(msg.id, suggestedReply)}
                                            >
                                              {copiedSuggestionForId === msg.id ? "Copied" : "Copy"}
                                            </button>
                                            <button
                                              type="button"
                                              className="admin-dash__email-reply-strip__suggest-btn"
                                              disabled={!to || sending}
                                              onClick={() => insertPlainSuggestedIntoComposer(suggestedReply)}
                                            >
                                              Insert in composer
                                            </button>
                                          </span>
                                        </div>
                                        <pre className="admin-dash__email-reply-strip__suggested-reply-body">
                                          {suggestedReply}
                                        </pre>
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()
                            : null}
                        </section>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="admin-dash__email-col admin-dash__email-col--compose">
          <div className="admin-dash__email-col-head">
            <h3 className="admin-dash__email-col-title">Compose</h3>
            <p className="admin-dash__email-col-hint">Rich text — formatted like a real client email</p>
          </div>

          <div className="admin-dash__email-compose-mail">
            <div className="admin-dash__email-compose-mail-rail" aria-hidden />
            <div className="admin-dash__email-compose-mail-inner">
              <div className="admin-dash__email-compose-mail-meta">
                <div className="admin-dash__email-compose-mail-row">
                  <span className="admin-dash__email-compose-mail-key">To</span>
                  {to ? (
                    <a className="admin-dash__link admin-dash__email-compose-mail-val" href={`mailto:${to}`}>
                      {to}
                    </a>
                  ) : (
                    <span className="admin-dash__td-muted">Save a client email on this record.</span>
                  )}
                </div>
                <div className="admin-dash__email-compose-mail-row">
                  <span className="admin-dash__email-compose-mail-key">Subject</span>
                  <input
                    className="admin-dash__email-compose-subject-input"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder={defaultSubject || "Subject"}
                    disabled={!to || sending}
                    autoComplete="off"
                  />
                </div>
                <div className="admin-dash__email-compose-mail-actions">
                  <button
                    type="button"
                    className="admin-dash__btn-text admin-dash__btn-text--small"
                    disabled={!to || sending || !(defaultSubject || "").trim()}
                    onClick={() => setSubject(defaultSubject || "")}
                  >
                    Reset subject
                  </button>
                </div>
                {suggestedClientReplyDraft ? (
                  <div className="admin-dash__email-compose-suggest" role="region" aria-label="AI suggested reply">
                    <p className="admin-dash__email-compose-suggest-lead">
                      {suggestedDraftSource === "request_ai"
                        ? "No client reply in this thread yet — insert a first outreach draft from the request’s AI analysis (edit before send)."
                        : "Insert the draft from the latest inbound message analysis into the composer (you can edit before send)."}
                    </p>
                    <div className="admin-dash__email-compose-suggest-actions">
                      <button
                        type="button"
                        className="admin-dash__btn-secondary admin-dash__btn-secondary--compact"
                        disabled={!to || sending}
                        onClick={() => {
                          const chunk = isProbablyHtml(suggestedClientReplyDraft)
                            ? sanitizeEmailHtml(suggestedClientReplyDraft)
                            : plainTextToEmailBodyHtml(suggestedClientReplyDraft);
                          setBodyHtml((prev) => {
                            const cur = (prev || "").trim();
                            if (!cur) return chunk;
                            return `${cur}<p><br /></p>${chunk}`;
                          });
                        }}
                      >
                        Insert suggested reply
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
              <form className="admin-dash__email-compose-mail-form" onSubmit={handleSubmit}>
                <AdminEmailRichComposer
                  disabled={!to || sending}
                  placeholder="Write your email — bold, lists, highlights, images…"
                  valueHtml={bodyHtml}
                  onChangeHtml={setBodyHtml}
                />
                <div className="admin-dash__email-compose-actions">
                  <button type="submit" className="admin-dash__btn-primary" disabled={!canSend}>
                    {sending ? "Sending…" : "Send email"}
                  </button>
                  <button
                    type="button"
                    className="admin-dash__btn-secondary"
                    disabled={bodyPlainLen === 0 || sending}
                    onClick={() => setBodyHtml("")}
                  >
                    Clear draft
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AdminEmailThreadPanel;
